import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { Box, Collapse, Divider, Grid, IconButton, Typography, useTheme } from '@mui/material'
import moment from 'moment'
import React, { Fragment, useState } from 'react'
import { ContaminantViolation } from '../../../../types/UtilityDetails'
import {
  DisinfectantIcon,
  HeavyMetalIcon,
  InorganicChemicalIcon,
  MicroorganismIcon,
  OrganicChemicalIcon,
  PfasIcon,
} from '../../../icons'
import { CheckCircleIcon } from '../../../icons/CheckCircleIcon'
import { DangerIcon } from '../../../icons/DangerIcon'
import { GhostIcon } from '../../../icons/GhostIcon'
import { VerifiedIcon } from '../../../icons/VerifiedIcon'
import SippTooltip from '../../../Tooltip/SippTooltip'

interface ContaminantViolationExtended extends ContaminantViolation {
  category: string
  icon: 'ghost' | 'warning'
  epaStandard: string
  description: string
  treatments: Array<{
    name: string
    description: string
  }>
}

type ContaminantType =
  | 'RULE VIOLATION'
  | 'CONTAMINANT'
  | 'ORGANIC CHEMICALS'
  | 'INORGANIC CHEMICALS'
  | 'MICROORGANISMS'
  | 'PFAS'
  | 'HEAVY METALS'
  | 'DISINFECTANT'
  | 'warning'

interface ContaminantDetailsProps {
  contaminantVols: ContaminantViolation[]
}

export const ContaminantDetails: React.FC<ContaminantDetailsProps> = ({ contaminantVols }) => {
  const theme = useTheme()
  const [expandedItem, setExpandedItem] = useState<number | null>(null)

  const handleToggle = (index: number) => {
    setExpandedItem((prev) => (prev === index ? null : index))
  }

  const getIcon = (type: ContaminantType) => {
    return type === 'CONTAMINANT' ? (
      <GhostIcon />
    ) : type === 'RULE VIOLATION' ? (
      <DangerIcon color="primary" />
    ) : type === 'ORGANIC CHEMICALS' ? (
      <OrganicChemicalIcon color="primary" />
    ) : type === 'INORGANIC CHEMICALS' ? (
      <InorganicChemicalIcon color="primary" />
    ) : type === 'MICROORGANISMS' ? (
      <MicroorganismIcon color="primary" />
    ) : type === 'PFAS' ? (
      <PfasIcon color="primary" />
    ) : type === 'HEAVY METALS' ? (
      <HeavyMetalIcon color="primary" />
    ) : type === 'DISINFECTANT' ? (
      <DisinfectantIcon color="primary" />
    ) : (
      <GhostIcon color="primary" />
    )
  }
  const contaminantType = (contaminant: ContaminantViolation): ContaminantType | string => {
    return contaminant.CONTAMINANT_DETAILS[0]?.contaminant_type
      ? contaminant.CONTAMINANT_DETAILS[0]?.contaminant_type?.toUpperCase()
      : contaminant?.CONTAMINANT_DETAIL.toLowerCase().includes('rule')
        ? 'RULE VIOLATION'
        : 'CONTAMINANT'
  }

  return (
    <Box
      sx={{
        width: '100%',
        borderRadius: '0.75rem',
        padding: '1rem',
        background: '#FFFFFF',
        mt: 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          position: 'relative',
        }}
      >
        <Typography fontSize="1rem" color="primary.dark" fontWeight={600}>
          Water Issues
        </Typography>
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            '&:hover > .tooltip-container': {
              visibility: 'visible',
              opacity: 1,
            },
          }}
        >
          <VerifiedIcon
            sx={{
              color: 'primary.main',
              fontSize: '1.25rem',
              cursor: 'pointer',
            }}
          />
          <Box
            className="tooltip-container"
            sx={{
              position: 'absolute',
              bottom: 'calc(100% + 10px)',
              left: '50%',
              transform: 'translateX(-50%)',
              visibility: 'hidden',
              opacity: 0,
              transition: 'visibility 0.2s, opacity 0.2s',
              zIndex: 9999,
              pointerEvents: 'none',
            }}
          >
            <SippTooltip />
          </Box>
        </Box>
      </Box>
      <Typography fontSize="0.875rem" color="text.secondary" fontWeight={500} sx={{ mt: 0.5 }}>
        The following water quality issues were detected in your water. Our{' '}
        <span style={{ color: theme.palette.primary.dark }}>SIPP</span> system is designed to
        address these concerns. Tap each issue to see how SIPP protects you.
      </Typography>
      <Divider sx={{ mt: 1 }} />
      {contaminantVols.map((contaminant, index) => (
        <React.Fragment key={`contaminant-${index}`}>
          <Box sx={{ mt: 2, px: 1 }}>
            <Grid container spacing={2}>
              <Grid item>
                <Box
                  sx={{
                    // bgcolor: 'grey.100',
                    bgcolor: 'transparent',
                    borderRadius: '50%',
                    width: 40,
                    height: 40,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {getIcon(contaminantType(contaminant) as ContaminantType)}
                </Box>
              </Grid>
              <Grid item xs>
                <Typography color="text.secondary" fontSize="0.75rem" fontWeight={500}>
                  {contaminantType(contaminant)}
                </Typography>
                <Typography fontSize="0.875rem" fontWeight={600}>
                  {contaminant.CONTAMINANT_DETAIL}
                </Typography>
              </Grid>
              <Grid item sx={{ textAlign: 'right' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                  <IconButton size="small" onClick={() => handleToggle(index)} sx={{ mr: -1.5 }}>
                    {expandedItem === index ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                  </IconButton>
                </Box>
              </Grid>
            </Grid>

            <Collapse in={expandedItem === index}>
              <Box sx={{ mt: 1 }}>
                {contaminant.CONTAMINANT_DETAILS &&
                  contaminant.CONTAMINANT_DETAILS.map((detail, index) => (
                    <Fragment key={index}>
                      <Typography variant="body2">Sources:</Typography>
                      <Typography color="text.primary" fontSize="0.75rem" sx={{ mt: 1 }}>
                        {detail.contaminant_sources}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Health Risks:
                      </Typography>
                      <Typography color="text.primary" fontSize="0.75rem" sx={{ mt: 1 }}>
                        {detail.potential_health_affects}
                      </Typography>
                    </Fragment>
                  ))}
                <Typography
                  color="text.secondary"
                  fontWeight={500}
                  fontSize="0.875rem"
                  sx={{ mt: 2, mb: 2 }}
                >
                  SIPP PROTECTION
                </Typography>
                {contaminant.CONTAMINANT_TREATMENTS.map((treatment, idx) => (
                  <Box key={idx} sx={{ display: 'flex', gap: 0, mb: 2, alignItems: 'flex-start' }}>
                    <CheckCircleIcon />
                    <Box sx={{ mt: -0.5 }}>
                      <Typography fontSize="0.875rem" fontWeight={600}>
                        {treatment}
                      </Typography>
                      {/* <Typography color="text.secondary" fontSize="0.75rem" fontWeight={500}>
                        {sippTreatments.description}
                      </Typography> */}
                    </Box>
                  </Box>
                ))}
                <Typography
                  color="text.secondary"
                  variant="caption"
                  sx={{ my: 0.25, display: 'inline-block', width: '100%' }}
                >
                  Violation first reported on:{' '}
                  {moment(contaminant.VIOL_FIRST_REPORTED_DATE).format('YYYY/MM/DD')}
                </Typography>
                <Typography color="text.secondary" variant="caption" sx={{ my: 0.25 }}>
                  Enforcement actions taken: {contaminant.ENFORCEMENT_ACTION_COUNT}
                </Typography>
              </Box>
            </Collapse>
          </Box>
          {index < contaminantVols.length - 1 && <Divider sx={{ mt: 2 }} />}
        </React.Fragment>
      ))}
    </Box>
  )
}
